/* @jsx h */
import { defineComponent, h, ref, onMounted, onBeforeUnmount, watch, computed, nextTick } from "vue";
import { RestaurantCustomer, CUSTOMER_STATUS_LEVELS } from "@lemmonhq/types";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { CustomerCRMCard } from "./components/CustomerCRMCard";
import { CustomerCRMDetail } from "./components/CustomerCRMDetail";
import { formatTagLabel } from "./utils";
import { BottomSheet, LemmonModal, useLocalization } from "@lemmonhq/uikit";

const STATUS_FILTERS = [
  { value: "", labelKey: "all", icon: "groups" },
  { value: CUSTOMER_STATUS_LEVELS.VIP, labelKey: "vip", icon: "star" },
  { value: CUSTOMER_STATUS_LEVELS.REGULAR, labelKey: "regular", icon: "person" },
  { value: CUSTOMER_STATUS_LEVELS.NEW, labelKey: "new", icon: "person_add" },
  {
    value: CUSTOMER_STATUS_LEVELS.INACTIVE,
    labelKey: "inactive",
    icon: "person_off",
  },
];

type SortOption = "top_spenders" | "most_visits" | "recent" | "name";

const SORT_OPTIONS: { value: SortOption; labelKey: string; icon: string }[] = [
  { value: "top_spenders", labelKey: "sort_top_spenders", icon: "payments" },
  { value: "most_visits", labelKey: "sort_most_visits", icon: "event_seat" },
  { value: "recent", labelKey: "sort_recent", icon: "schedule" },
  { value: "name", labelKey: "sort_name", icon: "sort_by_alpha" },
];

export const ReservationCustomersPage = defineComponent({
  name: "ReservationCustomersPage",
  setup() {
    const trpc = useTrpc();
    const { t } = useLocalization();
    const customers = ref<RestaurantCustomer[]>([]);
    const searchQuery = ref("");
    const selectedStatus = ref("");
    const selectedSort = ref<SortOption>("recent");
    const selectedTags = ref<string[]>([]);
    const availableTags = ref<string[]>([]);
    const selectedCustomer = ref<RestaurantCustomer | null>(null);
    const isLoading = ref(true);
    const isLoadingMore = ref(false);
    const loadError = ref<string | null>(null);
    const loadMoreTrigger = ref<HTMLElement | null>(null);
    const pagination = ref({ total: 0, limit: 20, offset: 0 });
    const showSortDropdown = ref(false);
    const showTagFilter = ref(false);
    const isMobile = ref(false);

    let observer: IntersectionObserver | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    let requestToken = 0;

    // Close dropdowns on outside click
    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (showSortDropdown.value && !target.closest("[data-sort-dropdown]")) {
        showSortDropdown.value = false;
      }
      if (showTagFilter.value && !target.closest("[data-tag-filter]")) {
        showTagFilter.value = false;
      }
    };

    const hasMore = computed(() => customers.value.length < pagination.value.total);

    // Server handles sorting — just pass through
    const sortedCustomers = computed(() => customers.value);

    const refreshLoadMoreObserver = async () => {
      await nextTick();
      if (!observer || !loadMoreTrigger.value) return;
      observer.unobserve(loadMoreTrigger.value);
      observer.observe(loadMoreTrigger.value);
    };

    const loadCustomers = async (reset = false) => {
      if (reset) {
        isLoading.value = true;
        isLoadingMore.value = false;
      } else {
        if (isLoading.value || isLoadingMore.value || !hasMore.value) return;
        isLoadingMore.value = true;
      }

      const token = ++requestToken;
      const offset = reset ? 0 : pagination.value.offset + pagination.value.limit;

      try {
        const response = await trpc.reservationCustomers.list.query({
          search: searchQuery.value.trim() || undefined,
          status_level: selectedStatus.value || undefined,
          tags: selectedTags.value.length ? [...selectedTags.value] : undefined,
          sort: selectedSort.value || undefined,
          limit: pagination.value.limit,
          offset,
        });

        if (token !== requestToken) return;

        const batch = response.data || [];
        if (reset) {
          customers.value = batch;
        } else {
          const existingIds = new Set(customers.value.map((c) => c.id));
          const newEntries = batch.filter((c) => !existingIds.has(c.id));
          if (newEntries.length) {
            customers.value = customers.value.concat(newEntries);
          }
        }

        pagination.value = {
          total: typeof response.pagination.total === "number" ? response.pagination.total : customers.value.length,
          limit: response.pagination.limit ?? 20,
          offset: response.pagination.offset ?? offset,
        };

      } catch (error) {
        console.error("Failed to load customers:", error);
        if (token === requestToken && reset) {
          loadError.value =
            error instanceof Error ? error.message : t("reservations.customers.failed_to_load_customers");
        }
      } finally {
        if (token === requestToken) {
          if (reset) isLoading.value = false;
          else isLoadingMore.value = false;
          void refreshLoadMoreObserver();
        }
      }
    };

    const tagsError = ref(false);
    const loadTags = async () => {
      try {
        tagsError.value = false;
        const tags = await trpc.reservationCustomers.tags.query();
        availableTags.value = Array.isArray(tags) ? tags : [];
      } catch (error) {
        console.error("Failed to load tags:", error);
        tagsError.value = true;
        availableTags.value = [];
      }
    };

    const scheduleReload = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => loadCustomers(true), 300);
    };

    watch(() => searchQuery.value, scheduleReload);
    watch(() => selectedStatus.value, scheduleReload);
    watch(() => selectedSort.value, scheduleReload);
    watch(() => selectedTags.value.slice().sort().join(","), scheduleReload);

    const toggleTag = (tag: string) => {
      const idx = selectedTags.value.indexOf(tag);
      if (idx > -1) selectedTags.value.splice(idx, 1);
      else selectedTags.value.push(tag);
    };

    const handleSelectCustomer = (customer: RestaurantCustomer) => {
      selectedCustomer.value = customer;
    };

    const handleCloseDetail = () => {
      selectedCustomer.value = null;
    };

    const handleCustomerUpdated = (updated: RestaurantCustomer) => {
      const idx = customers.value.findIndex((c) => c.id === updated.id);
      if (idx !== -1) {
        // Merge to preserve aggregate stats not returned by update API
        customers.value[idx] = { ...customers.value[idx], ...updated };
        selectedCustomer.value = customers.value[idx];
      } else {
        selectedCustomer.value = updated;
      }
    };

    const updateIsMobile = () => {
      isMobile.value = window.innerWidth < 768;
    };

    const activeFilterCount = computed(() => {
      let count = 0;
      if (selectedStatus.value) count++;
      if (selectedTags.value.length) count++;
      if (searchQuery.value.trim()) count++;
      return count;
    });

    onMounted(async () => {
      updateIsMobile();
      window.addEventListener("resize", updateIsMobile);
      document.addEventListener("click", handleDocumentClick);
      await Promise.all([loadCustomers(true), loadTags()]);
      await nextTick();

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && hasMore.value && !isLoading.value && !isLoadingMore.value) {
              loadCustomers(false);
            }
          });
        },
        { rootMargin: "200px 0px" },
      );

      if (loadMoreTrigger.value) {
        observer.observe(loadMoreTrigger.value);
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", updateIsMobile);
      document.removeEventListener("click", handleDocumentClick);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    });

    return () => (
      <div class="flex flex-1 h-full overflow-hidden">
        {/* ─── Left Panel — Customer List ─── */}
        <div class="flex w-full min-w-0 md:w-[340px] md:min-w-[280px] xl:w-[400px] md:border-r border-base-content/8 flex-col bg-base-200/20">
          {/* Header */}
          <div class="px-4 pt-4 pb-3 space-y-3 border-b border-base-content/8 bg-base-200">
            {/* Title Row */}
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-primary text-xl">group</span>
                <h2 class="text-base font-semibold text-accent">{t("reservations.customers.title")}</h2>
                <span class="badge badge-sm badge-ghost font-mono">{pagination.value.total}</span>
              </div>
              <div class="flex items-center gap-1">
                {/* Sort Button */}
                <div class="relative" data-sort-dropdown>
                  <button
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation();
                      showSortDropdown.value = !showSortDropdown.value;
                    }}
                    class={`btn btn-sm btn-secondary btn-square ${selectedSort.value !== "recent" ? "text-primary" : ""}`}
                    aria-label={t("reservations.customers.sort_customers")}
                  >
                    <span class="material-symbols-outlined text-[16px]">
                      {SORT_OPTIONS.find((o) => o.value === selectedSort.value)?.icon ?? "sort"}
                    </span>
                  </button>
                  {showSortDropdown.value && (
                    <div class="absolute right-0 top-9 z-50 bg-base-200 border border-base-content/10 rounded-lg shadow-xl py-1 w-44">
                      {SORT_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => {
                            selectedSort.value = selectedSort.value === opt.value ? "recent" : opt.value;
                            showSortDropdown.value = false;
                          }}
                          class={`flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-base-300 transition-colors ${
                            selectedSort.value === opt.value ? "text-primary font-medium" : "text-base-content/60"
                          }`}
                        >
                          <span class="material-symbols-outlined text-[14px]">{opt.icon}</span>
                          {t(`reservations.customers.${opt.labelKey}`)}
                          {selectedSort.value === opt.value && (
                            <span class="material-symbols-outlined text-[12px] ml-auto">check</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Tag Filter Button */}
                {availableTags.value.length > 0 && (
                  <button
                    data-tag-filter
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation();
                      showTagFilter.value = !showTagFilter.value;
                    }}
                    class={`btn btn-sm btn-square ${selectedTags.value.length > 0 ? "btn-primary" : "btn-ghost"}`}
                    aria-label={t("reservations.customers.filter_by_tags")}
                  >
                    <span class="material-symbols-outlined text-[16px]">label</span>
                  </button>
                )}
              </div>
            </div>

            {/* Search */}
            <div class="relative">
              <span class="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-base-content/30 text-[16px]">
                search
              </span>
              <input
                class="input input-bordered input-sm w-full pl-9 pr-8 bg-base-100/40 focus:bg-base-100 transition-colors text-sm"
                type="text"
                placeholder={t("reservations.customers.search_placeholder")}
                value={searchQuery.value}
                onInput={(e) => (searchQuery.value = (e.target as HTMLInputElement).value)}
              />
              {searchQuery.value && (
                <button
                  type="button"
                  class="btn btn-xs btn-ghost btn-circle absolute right-1 top-1/2 -translate-y-1/2"
                  onClick={() => (searchQuery.value = "")}
                >
                  <span class="material-symbols-outlined text-xs">close</span>
                </button>
              )}
            </div>

            {/* Status Filter Pills */}
            <div class="flex gap-1 flex-wrap">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => (selectedStatus.value = filter.value)}
                  class={`btn btn-xs gap-1 rounded-full transition-colors duration-100 min-h-[28px] ${
                    selectedStatus.value === filter.value
                      ? filter.value === CUSTOMER_STATUS_LEVELS.VIP
                        ? "bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/30"
                        : "btn-primary"
                      : "btn-ghost bg-base-100/30 hover:bg-base-100/60"
                  }`}
                >
                  <span class="material-symbols-outlined text-[12px]">{filter.icon}</span>
                  {t(`reservations.customers.${filter.labelKey}`)}
                </button>
              ))}
            </div>

            {/* Tag Filters (expandable) */}
            {showTagFilter.value && availableTags.value.length > 0 && (
              <div class="pt-1 border-t border-base-content/5" data-tag-filter>
                <div class="text-[10px] text-base-content/30 uppercase tracking-wider mb-1.5">
                  {t("reservations.customers.tags")}
                </div>
                <div class="flex flex-wrap gap-1">
                  {availableTags.value.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      class={`badge badge-xs transition-colors duration-100 cursor-pointer ${
                        selectedTags.value.includes(tag) ? "badge-primary" : "badge-ghost hover:badge-primary/30"
                      }`}
                    >
                      {formatTagLabel(tag)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Active Filters Indicator */}
            {activeFilterCount.value > 0 && (
              <button
                onClick={() => {
                  searchQuery.value = "";
                  selectedStatus.value = "";
                  selectedTags.value = [];
                }}
                class="flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 transition-colors duration-100 bg-primary/8 rounded-full px-2 py-1 min-h-[32px]"
              >
                <span class="material-symbols-outlined text-[12px]">filter_list_off</span>
                {t(
                  activeFilterCount.value > 1
                    ? "reservations.customers.clear_filters"
                    : "reservations.customers.clear_filter",
                  [activeFilterCount.value],
                )}
              </button>
            )}
          </div>

          {/* Customer Cards List */}
          <div class="flex-1 overflow-y-auto">
            {isLoading.value ? (
              <div class="p-3 space-y-2">
                {[...Array(6)].map((_, i) => (
                  <div key={i} class="rounded-lg bg-base-100/30 p-3 animate-pulse motion-reduce:animate-none">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-full bg-base-content/8" />
                      <div class="flex-1 space-y-2">
                        <div class="h-3.5 w-28 bg-base-content/8 rounded" />
                        <div class="h-2.5 w-36 bg-base-content/5 rounded" />
                      </div>
                      <div class="space-y-2 text-right">
                        <div class="h-3.5 w-14 bg-base-content/8 rounded ml-auto" />
                        <div class="h-2.5 w-20 bg-base-content/5 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : loadError.value ? (
              <div class="flex items-center justify-center h-64">
                <div class="text-center space-y-3 px-8">
                  <div class="w-14 h-14 rounded-xl bg-error/8 flex items-center justify-center mx-auto">
                    <span class="material-symbols-outlined text-2xl text-error/60">error</span>
                  </div>
                  <p class="text-sm font-medium text-base-content/50">
                    {t("reservations.customers.failed_to_load_customers")}
                  </p>
                  <p class="text-xs text-base-content/30">{loadError.value}</p>
                  <button
                    onClick={() => {
                      loadError.value = null;
                      loadCustomers(true);
                    }}
                    class="btn btn-xs btn-primary gap-1"
                  >
                    <span class="material-symbols-outlined text-[14px]">refresh</span>
                    {t("reservations.customers.try_again")}
                  </button>
                </div>
              </div>
            ) : sortedCustomers.value.length === 0 ? (
              <div class="flex items-center justify-center h-64">
                <div class="text-center space-y-3 px-8">
                  <div class="w-14 h-14 rounded-xl bg-base-content/5 flex items-center justify-center mx-auto">
                    <span class="material-symbols-outlined text-2xl text-base-content/20">person_search</span>
                  </div>
                  <p class="text-sm font-medium text-base-content/40">
                    {t("reservations.customers.no_customers_found")}
                  </p>
                  <p class="text-xs text-base-content/25 leading-relaxed">
                    {activeFilterCount.value > 0
                      ? t("reservations.customers.adjust_search_or_filters")
                      : t("reservations.customers.empty_description")}
                  </p>
                </div>
              </div>
            ) : (
              <div class="p-2 space-y-1">
                {sortedCustomers.value.map((customer) => (
                  <CustomerCRMCard
                    key={customer.id}
                    customer={customer}
                    isSelected={selectedCustomer.value?.id === customer.id}
                    onSelect={handleSelectCustomer}
                  />
                ))}

                <div ref={loadMoreTrigger} class="h-2 w-full" />

                {isLoadingMore.value && (
                  <div class="flex justify-center py-3">
                    <div class="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                  </div>
                )}

                {!hasMore.value && customers.value.length > 5 && (
                  <p class="text-center text-[10px] text-base-content/20 py-3">
                    {t("reservations.customers.customers_loaded", [customers.value.length])}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ─── Right Panel — Customer Detail / Empty State ─── */}
        <div class="hidden md:block min-w-0 flex-1 overflow-hidden bg-base-300/15">
          {selectedCustomer.value ? (
            <CustomerCRMDetail
              customer={selectedCustomer.value}
              onClose={handleCloseDetail}
              onUpdated={handleCustomerUpdated}
            />
          ) : (
            <div class="flex items-center justify-center h-full">
              <div class="text-center space-y-6 max-w-md px-8">
                <div class="relative mx-auto w-24 h-24">
                  <div class="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 rotate-6" />
                  <div class="absolute inset-0 rounded-2xl bg-base-200/80 flex items-center justify-center">
                    <span class="material-symbols-outlined text-5xl text-base-content/15">contact_page</span>
                  </div>
                </div>
                <div class="space-y-2">
                  <h3 class="text-xl font-bold text-base-content/50">{t("reservations.customers.crm_title")}</h3>
                  <p class="text-sm text-base-content/30 leading-relaxed">
                    {t("reservations.customers.crm_description")}
                  </p>
                </div>
                <div class="grid grid-cols-3 gap-3 pt-2">
                  {[
                    {
                      icon: "payments",
                      label: t("reservations.customers.spending"),
                      desc: t("reservations.customers.order_history"),
                    },
                    {
                      icon: "event_seat",
                      label: t("reservations.customers.reservations"),
                      desc: t("reservations.customers.past_and_upcoming"),
                    },
                    {
                      icon: "star",
                      label: t("reservations.customers.loyalty"),
                      desc: t("reservations.customers.vip_and_notes"),
                    },
                  ].map((item) => (
                    <div key={item.icon} class="bg-base-200/40 rounded-lg p-3 space-y-1 border border-base-content/5">
                      <span class="material-symbols-outlined text-lg text-base-content/20">{item.icon}</span>
                      <div class="text-xs font-medium text-base-content/35">{item.label}</div>
                      <div class="text-[10px] text-base-content/20">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {isMobile.value && selectedCustomer.value ? (
          <LemmonModal
            onClose={handleCloseDetail}
            height="calc(100dvh - 16px)"
            class="bg-base-300"
            disableBottomSheet={true}
            title={selectedCustomer.value.name || undefined}
          >
            <CustomerCRMDetail
              customer={selectedCustomer.value}
              onClose={handleCloseDetail}
              onUpdated={handleCustomerUpdated}
              hideCloseButton={true}
              hideName={true}
            />
          </LemmonModal>
        ) : null}
      </div>
    );
  },
});
